/**
 * Balão de fala em cima da cabeça, como no Ultima Online (techspec-balao-de-fala).
 *
 * Quem desenha o boneco marca a âncora do balão a cada quadro (`ancorarBalao`); no fim do quadro
 * `desenharBaloes` pinta tudo por cima do mundo, para nenhum sprite cobrir a fala.
 * As palavras de poder e o "falhou" continuam no texto curto do motor: magia não é conversa.
 */

export type EstiloBalao = 'balao' | 'classico' | 'nada';

const KEY = 'uoidle.balao';
const ESTILOS: readonly EstiloBalao[] = ['balao', 'classico', 'nada'];

let estilo: EstiloBalao = 'balao';

try {
  const salvo = localStorage.getItem(KEY);
  if (salvo && (ESTILOS as readonly string[]).includes(salvo)) estilo = salvo as EstiloBalao;
} catch {
  /* sem storage: fica o padrão */
}

export function estiloBalao(): EstiloBalao {
  return estilo;
}

export function aplicarEstiloBalao(novo: EstiloBalao) {
  if (!ESTILOS.includes(novo)) return;
  estilo = novo;
  try { localStorage.setItem(KEY, novo); } catch { /* ignore */ }
}

/** Até 3 falas por pessoa; a mais nova embaixo, perto da cabeça. */
const POR_PESSOA = 3;
const MAX_LINHAS = 4;

interface Fala { texto: string; nasceu: number; ate: number }

const falas = new Map<number, Fala[]>();
const ancoras = new Map<number, { x: number; y: number }>();

/** 3,5 s + 60 ms por letra, até 8 s. */
export function duracaoDaFala(texto: string): number {
  return Math.min(8000, 3500 + texto.length * 60);
}

export function falarBalao(id: number, texto: string, agora = performance.now()) {
  const limpo = texto.trim();
  if (!limpo) return;
  const lista = falas.get(id) ?? [];
  lista.push({ texto: limpo, nasceu: agora, ate: agora + duracaoDaFala(limpo) });
  while (lista.length > POR_PESSOA) lista.shift();
  falas.set(id, lista);
}

export function calarBalao(id: number) {
  falas.delete(id);
  ancoras.delete(id);
}

export function calarTodos() {
  falas.clear();
  ancoras.clear();
}

export function temFala(id: number): boolean {
  return falas.has(id);
}

/** Zera as âncoras: quem não foi desenhado neste quadro não ganha balão. */
export function novoQuadroBaloes() {
  ancoras.clear();
}

export function ancorarBalao(id: number, x: number, y: number) {
  if (falas.has(id)) ancoras.set(id, { x, y });
}

/** `*acena*` é gesto: itálico lilás, no balão e no chat. */
export function ehGesto(texto: string): boolean {
  return /^\*[^*]+\*$/.test(texto.trim());
}

function quebrar(ctx: CanvasRenderingContext2D, texto: string, larg: number): string[] {
  const linhas: string[] = [];
  let atual = '';
  for (const p of texto.split(/\s+/)) {
    const tentativa = atual ? `${atual} ${p}` : p;
    if (!atual || ctx.measureText(tentativa).width <= larg) atual = tentativa;
    else { linhas.push(atual); atual = p; }
  }
  if (atual) linhas.push(atual);
  if (linhas.length > MAX_LINHAS) {
    const corte = linhas.slice(0, MAX_LINHAS);
    corte[MAX_LINHAS - 1] = `${corte[MAX_LINHAS - 1]}…`;
    return corte;
  }
  return linhas;
}

function fonte(gesto: boolean) {
  return `${gesto ? 'italic ' : ''}600 13px Spectral, Georgia, serif`;
}

/** Pergaminho escuro com borda de ouro; rabicho só no mais novo. Devolve o topo para empilhar. */
function pintarBalao(
  ctx: CanvasRenderingContext2D, texto: string, x: number, base: number, alfa: number,
  rabicho: boolean, largura: number, larguraTela: number
): number {
  const gesto = ehGesto(texto);
  ctx.save();
  ctx.globalAlpha = alfa;
  ctx.font = fonte(gesto);
  const linhas = quebrar(ctx, texto, largura);
  const lh = 16, padX = 9, padY = 6, r = 6;
  const w = Math.max(...linhas.map(l => ctx.measureText(l).width)) + padX * 2;
  const h = linhas.length * lh + padY * 2;
  const rab = rabicho ? 6 : 0;
  const x0 = Math.max(4, Math.min(larguraTela - w - 4, Math.round(x - w / 2)));
  const y0 = Math.round(base - h - rab);
  const ponta = Math.max(x0 + r + 6, Math.min(x0 + w - r - 6, x));
  ctx.beginPath();
  ctx.moveTo(x0 + r, y0);
  ctx.arcTo(x0 + w, y0, x0 + w, y0 + h, r);
  ctx.arcTo(x0 + w, y0 + h, x0, y0 + h, r);
  if (rabicho) {
    ctx.lineTo(ponta + 6, y0 + h);
    ctx.lineTo(ponta, y0 + h + rab);
    ctx.lineTo(ponta - 6, y0 + h);
  }
  ctx.arcTo(x0, y0 + h, x0, y0, r);
  ctx.arcTo(x0, y0, x0 + w, y0, r);
  ctx.closePath();
  ctx.fillStyle = 'rgba(16,14,10,.88)';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(201,162,39,.75)';
  ctx.stroke();
  ctx.fillStyle = gesto ? '#d9b8ff' : '#f2e8cf';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  linhas.forEach((l, i) => ctx.fillText(l, x0 + w / 2, y0 + padY + i * lh));
  ctx.restore();
  return y0 - 3;
}

/** Clássico do UO: só o texto com contorno preto. */
function pintarClassico(
  ctx: CanvasRenderingContext2D, texto: string, x: number, base: number, alfa: number, largura: number
): number {
  const gesto = ehGesto(texto);
  ctx.save();
  ctx.globalAlpha = alfa;
  ctx.font = fonte(gesto);
  const linhas = quebrar(ctx, texto, largura);
  const lh = 15;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(0,0,0,.85)';
  ctx.fillStyle = gesto ? '#d9b8ff' : '#f0d878';
  let y = base;
  for (let i = linhas.length - 1; i >= 0; i--) {
    ctx.strokeText(linhas[i], x, y);
    ctx.fillText(linhas[i], x, y);
    y -= lh;
  }
  ctx.restore();
  return y - 2;
}

export function desenharBaloes(ctx: CanvasRenderingContext2D, larguraTela: number, agora: number) {
  if (falas.size === 0) return;
  const largura = larguraTela < 600 ? 170 : 210;
  for (const [id, lista] of falas) {
    for (let i = lista.length - 1; i >= 0; i--) if (lista[i].ate <= agora) lista.splice(i, 1);
    if (lista.length === 0) { falas.delete(id); continue; }
    if (estilo === 'nada') continue;
    const a = ancoras.get(id);
    if (!a) continue;
    let base = a.y;
    for (let i = lista.length - 1; i >= 0; i--) {
      const f = lista[i];
      const alfa = Math.max(0, Math.min(1, (f.ate - agora) / 400, (agora - f.nasceu) / 120));
      base = estilo === 'balao'
        ? pintarBalao(ctx, f.texto, a.x, base, alfa, i === lista.length - 1, largura, larguraTela)
        : pintarClassico(ctx, f.texto, a.x, base, alfa, largura);
    }
  }
}
